/**
 * TracesListModal Component
 *
 * This component displays a modal with a list of traces and their details.
 * It allows users to view, add, edit, resolve, and unresolve traces.
 *
 * @component
 * @param {Object} props - The component props
 * @param {any} props.referenceID - The reference ID for the traces
 * @param {boolean} [props.isOpen=true] - Whether the modal is open
 * @param {Function} [props.onClose] - Function to call when closing the modal
 * @param {string} [props.traceTarget] - The target of the trace
 */

import useDriver from '@hooks/useDriver';
import { Circle, Send } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  Chip,
  debounce,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GridColDef,
  GridFilterModel,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { addFAQButton, simulateClick } from '@utils/guide';
import moment, { Moment } from 'moment';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useCreateTracesNotesMutation,
  useLazyGetTraceDataQuery,
} from '../../api/traces/traces.api.rtk';
import { getColor } from '../../assets/theme';
import useMenu from '../../hooks/useMenu';
import { ModalWrapper } from '../../hooks/useModal';
import useToast from '../../hooks/useToast';
import { useAppSelector } from '../../store';
import {
  selectCurrentModuleDesc,
  selectDepartmentList,
} from '../../store/selector/property.selector';
import { selectCurrentUserID } from '../../store/selector/user.selector';
import DataTable from '../table/datatable';
import TracesStatusChips from './chipTracesStatus';
import TracesCreateEditDrawer from './traces-create-edit';
import TracesResolveConfirmationModal from './traces-resolve-confirmation-modal';
import TracesUnresolveConfirmationModal from './traces-unresolve-confirmation-modal';
import { useCurrentModule } from '@hooks/useCurrentModule';

const TracesListModal = ({
  referenceID,
  isOpen,
  onClose,
  traceTarget,
}: {
  referenceID?: any;
  isOpen: boolean;
  onClose: () => void;
  loadingComponent?: any;
  traceTarget?: string;
}) => {
  const [createTraceNotes] = useCreateTracesNotesMutation();
  const { toast } = useToast();

  const [viewTraces, setViewTraces] = useState<Boolean>(false);
  const [selectedTarget, setSelectedTarget] = useState<any>();

  /**
   * Handles the selection change in the data grid
   * @param {GridRowSelectionModel} selectionModel - The new selection model
   */
  const handleSelectionChange = useCallback(
    (selectionModel: GridRowSelectionModel) => {
      if (selectionModel.length > 0) {
        const [value] = selectionModel;
        setSelectedTarget(value);
        setViewTraces(true);
      }
    },
    [],
  );

  const [tracesDetails, setTracesDetails] = useState<any>(null);
  // Define parameters based on condition
  const p_referenceID = referenceID ? referenceID : '';
  const [
    getTraces,
    {
      isLoading: isGetting,
      isFetching: isGetFetching,
      data: getTracesData,
      isError: isError,
    },
  ] = useLazyGetTraceDataQuery();

  /**
   * Refetches the trace data
   */
  const refetchData = async () => {
    (async () => {
      await getTraces(p_referenceID)
        .unwrap()
        .then((res: any) => {
          setTracesDetails(res.data);
        })
        .catch((err: any) => console.log(err));
    })();
  };

  useEffect(() => {
    if (isOpen) {
      (async () => {
        await refetchData();
      })();
    }
    return () => {
      setTracesDetails(null);
    };
  }, [isOpen, getTracesData]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  /**
   * Opens the create or edit trace drawer
   */
  const createOrEditTrace = () => {
    setIsDrawerOpen(true);
    handleClose();
  };

  /**
   * Closes the create or edit trace drawer
   */
  const handleCloseCreateOrEditTrace = () => {
    setIsDrawerOpen(false);
  };

  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);

  /**
   * Opens the resolve trace modal
   */
  const resolveTrace = () => {
    setIsResolveModalOpen(true);
    handleClose();
  };

  /**
   * Closes the resolve trace modal
   */
  const handleCloseResolveTrace = () => {
    setIsResolveModalOpen(false);
  };

  const [isUnResolveModalOpen, setIsUnResolveModalOpen] = useState(false);

  /**
   * Opens the unresolve trace modal
   */
  const unResolveTrace = () => {
    setIsUnResolveModalOpen(true);
    handleClose();
  };

  /**
   * Closes the unresolve trace modal
   */
  const handleCloseUnResolveTrace = () => {
    setIsUnResolveModalOpen(false);
  };

  const [isOpenModal, setIsOpenModal] = useState(isOpen);

  useEffect(() => {
    setIsOpenModal(isOpen);
  }, [isOpen]);

  /**
   * Handles closing the modal
   */
  const handleCloseClick = () => {
    setIsOpenModal(false);
    onClose && onClose();
  };

  const [activeStatus, setActiveStatus] = useState<string>('All');

  const selectedProperty = useAppSelector(
    (state) => state.properties.selectedProperty,
  );

  const departmentList = useAppSelector(selectDepartmentList);
  const listDepartment = departmentList.filter(
    (item: any) => item == 'Front Office' || item == 'Users Management',
  );
  const currentModule = useCurrentModule();
  const currentModuleDesc = useAppSelector((state) =>
    selectCurrentModuleDesc(state, currentModule),
  );
  const currentUserID = useAppSelector(selectCurrentUserID);

  const [selectedStartDate, setSelectedStartDate] = useState<Moment | null>(
    moment(),
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Moment | null>(
    moment(),
  );
  const apiRef = useGridApiRef();

  const [departmentListValue, setDepartmentListValue] = useState<string>('');
  const [settingsInputValue, setSettingsInputValue] = useState('');

  /**
   * Handles the change in department list
   * @param {ChangeEvent<HTMLInputElement>} event - The change event
   */
  const handleDepartmentListChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    updateSearchDepartment(newValue);
  };

  /**
   * Updates the search based on department
   */
  const updateSearchDepartment = useMemo(() => {
    return debounce((status) => {
      const newFilterModel: GridFilterModel = {
        items: [
          {
            field: 'department',
            operator: 'contains',
            value: status,
          },
        ],
      };
      apiRef?.current?.setFilterModel(newFilterModel);
    }, 500);
  }, [apiRef]);

  /**
   * Updates the search based on date from
   */
  const updateSearchDateFrom = useMemo(() => {
    return debounce((dateFrom) => {
      const newFilterModel: GridFilterModel = {
        items: [
          {
            field: 'date',
            operator: 'startsWith',
            value: dateFrom,
          },
        ],
      };
      apiRef?.current?.setFilterModel(newFilterModel);
    }, 500);
  }, [apiRef]);

  /**
   * Handles the click on status chips
   * @param {string} status - The selected status
   */
  const handleStatusClick = (status: string) => {
    setActiveStatus(status);
    updateSearchStatus(status);
  };

  /**
   * Updates the search based on status
   */
  const updateSearchStatus = useMemo(() => {
    return debounce((status) => {
      let newFilterModel: GridFilterModel | null = null;
      if (status === 'All') {
        newFilterModel = {
          items: [
            {
              field: 'date',
              operator: 'isNotEmpty',
            },
          ],
        };
      } else if (status === 'Active') {
        const currentDate = moment().format('YYYY-MM-DD');
        newFilterModel = {
          items: [
            {
              field: 'date',
              operator: 'is',
              value: currentDate,
            },
          ],
        };
      } else if (status === 'Pending') {
        const currentDate = moment().format('YYYY-MM-DD');

        newFilterModel = {
          items: [
            {
              field: 'date',
              operator: 'after',
              value: currentDate,
            },
          ],
        };
      }

      if (newFilterModel !== null) {
        apiRef?.current?.setFilterModel(newFilterModel);
      }
    }, 500);
  }, [apiRef]);

  const {
    handleContextMenu,
    removeSelectedData,
    selectedData,
    handleClose,
    MenuWrapper,
    handleTouchEnd,
    handleTouchStart,
  } = useMenu();

  /**
   * Defines the columns for the data grid
   */
  const columns: GridColDef[] = [
    {
      field: 'traceTarget',
      headerName: 'Trace Target',
      minWidth: 170,
      sortable: false,
      renderCell(params) {
        const date = moment(params.row?.date).format('YYYY-MM-DD');
        const currentDate = moment().format('YYYY-MM-DD');
        const isCurrentDate = date === currentDate;
        return (
          <Stack direction='row' gap='6px' alignItems='center'>
            <Circle
              sx={{
                fontSize: '10px',
                color: isCurrentDate ? success : orange,
              }}
            />
            {params.value}
          </Stack>
        );
      },
    },
    {
      type: 'date',
      field: 'date',
      headerName: 'Date',
      minWidth: 50,
      sortable: false,
      valueFormatter: ({ value }) => {
        const date = moment(value);
        return date.format('MMM DD, YYYY');
      },
      valueGetter: (params) => new Date(params.value),
    },
    {
      field: 'recurringTime',
      headerName: 'Trace Time',
      minWidth: 110,
      sortable: false,
      valueFormatter: ({ value }) => {
        const time = moment(value, 'HH:mm:ss.SSS');
        return time.format('hh:mm A');
      },
      type: 'dateTime',
      valueGetter: ({ value }) => {
        return moment(value, 'HH:mm:ss.SSS').toDate();
      },
    },
    {
      field: 'displayTime',
      headerName: 'Notif Time',
      minWidth: 150,
      sortable: false,
      valueFormatter: ({ value }) => {
        const time = moment(value, 'HH:mm:ss.SSS');
        return time.format('hh:mm A');
      },
      type: 'dateTime',
      valueGetter: ({ value }) => {
        return moment(value, 'HH:mm:ss.SSS').toDate();
      },
    },
    {
      field: 'department',
      headerName: 'Department',
      minWidth: 120,
      sortable: false,
    },
    {
      field: 'message',
      headerName: 'Trace Message',
      minWidth: 370,
      align: 'left',
      sortable: false,
      renderCell: (params) => (
        <span
          style={{
            display: 'inline-block',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: '370px',
          }}
          title={params.value}
        >
          {params.value}
        </span>
      ),
    },
  ];

  let canResolved = false;
  let isActive = false;
  let userCanEdit = false;
  if (selectedData != null && selectedData != undefined) {
    const currentDate = moment().format('YYYY-MM-DD');
    const foundTrace = getTracesData?.data?.traces?.find(
      (trace: any) => trace?.trace?.traceDetailsID == selectedData,
    );
    if (foundTrace?.trace?.department === currentModuleDesc) {
      canResolved = true;
    }
    if (foundTrace?.trace?.userID === currentUserID) {
      userCanEdit = true;
    }
    if (foundTrace?.trace?.date === currentDate) {
      isActive = true;
    }
  }

  const { bgColor, success, orange, white } = getColor();
  const matchingTrace = getTracesData?.data?.traces?.find(
    (trace: any) => trace?.trace?.traceDetailsID == selectedTarget,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [matchingTrace?.trace?.notes]);

  const [commentValue, setCommentValue] = useState('');

  /**
   * Handles the change in comment input
   * @param {any} event - The change event
   */
  const handleChange = (event: any) => {
    setCommentValue(event.target.value);
    setError(false);
  };
  const [error, setError] = useState(false);

  /**
   * Handles the submission of a new comment
   */
  const handleSubmit = async () => {
    if (commentValue.trim() === '') {
      setError(true);
      return;
    }
    const jsonData = {
      properties: [
        {
          property: {
            propertyID: selectedProperty.value,
            notes: [
              {
                note: {
                  traceDetailsID: matchingTrace?.trace?.traceDetailsID,
                  message: commentValue,
                },
              },
            ],
          },
        },
      ],
    };
    await createTraceNotes(jsonData)
      .unwrap()
      .then(() => {
        toast({ severity: 'success', message: 'Success on sending notes' });
      })
      .catch((err) =>
        toast({
          severity: 'error',
          message: err,
        }),
      )
      .finally(() => {
        setCommentValue('');
        refetchData();
      });
  };

  // #region GUIDE
  const { driver, defaultConfig } = useDriver();
  const [_onGuide, setOnGuide] = useState(false);

  const handleGuide = () => {
    const driverObj = driver({
      ...defaultConfig,
      steps: [
        {
          element: '#trace-list_add-new-btn',
          popover: {
            title: 'Create a trace',
            description: `
            <div style="max-height: 200px; overflow:auto;">
            <b>Traces</b> are used for inter-departmental communication and endorsements. They can represent requests or services that need to be provided to guests either at the time of reservation or during their stay.

            </br></br>The department receiving the request logs the details as a Trace in the reservation system, notifying the responsible department to take action at the specified time and date. Once the request or service has been fulfilled, the issuing department updates the Trace status to Resolved.

            </br></br>This guide will walk you through the process of creating traces on the Xenia Cloud Property Management System

            </br></br>The <b>Traces</b> window will list all the current and upcoming traces that will also appear on the <b>Notifications</b> on the top-right bell icon of the screen. <b>Click Add New</b> to create a new <b>trace.</b>
            </div>`,
            onNextClick: () => {
              simulateClick('#trace-list_add-new-btn');

              setTimeout(() => {
                driverObj.moveNext();
              }, 100);
            },
          },
        },
        {
          element: '#traces-create-edit_modal .MuiStack-root',
          popover: {
            title: 'Create a trace',
            description:
              '<b>Input the required details</b> for the trace, including the relevant department, trace dates and time, notification time, and the message. Once complete, <b>click Save.</b>',
            onPrevClick: () => {
              simulateClick('#traces-create-edit_modal-close-btn');
              setTimeout(() => {
                driverObj.movePrevious();
              }, 100);
            },
          },
        },
        {
          element: '#traces-create-edit_save-btn',
          popover: {
            title: 'Create a trace',
            description:
              '<b>Input the required details</b> for the trace, including the relevant department, trace dates and time, notification time, and the message. Once complete, <b>click Save.</b>',
            onNextClick: () => {
              simulateClick('#traces-create-edit_modal-close-btn');

              setTimeout(() => {
                driverObj.moveNext();
              }, 100);
            },
            onPopoverRender: (popover) =>
              addFAQButton({
                popover,
                link: 'https://servoitsolutions.ph/support/kb/faq.php?id=1955',
                driverInstance: driverObj,
              }),
          },
        },
      ],
      onDestroyed: () => {
        setOnGuide(false);
      },
      onHighlightStarted: () => {
        setOnGuide(true);
      },
    });

    driverObj.drive();
  };
  // #endregion

  return (
    <>
      <ModalWrapper
        {...{
          modalDisclosure: {
            isOpen: isOpenModal,
            onClose: handleCloseClick,
            onToggle: () => setIsOpenModal(!isOpenModal),
            onOpen: () => setIsOpenModal(true),
          },
          disableOnClose: true,
          withHeader: {
            title: 'Traces',
            onClose: handleCloseClick,
            learnMore: handleGuide,
          },
          sx: { '.with-header-main': { p: 0, gap: 0 } },
        }}
      >
        <Stack
          sx={{
            flex: 1,
            maxHeight: '90dvh',
            position: 'relative',
            minHeight: '80vh',
          }}
        >
          {/* MAIN */}
          <Stack
            flex={1}
            minHeight={0}
            sx={{ p: 3, pb: 1, overflow: 'auto' }}
            gap={1}
            direction={{ xs: 'column', lg: 'row' }}
          >
            <Stack gap={2} flex={1} minHeight={0}>
              {/* DATE */}
              <Stack
                gap={1}
                direction={{ xs: 'column', md: 'row' }}
                width='100%'
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                      },
                    }}
                    format='MMM DD, YYYY'
                    label='Date from'
                    value={selectedStartDate}
                    onChange={(newValue: any) => {
                      setSelectedStartDate(newValue);
                      updateSearchDateFrom(newValue);
                    }}
                    disabled
                  />
                  <DatePicker
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                      },
                    }}
                    format='MMM DD, YYYY'
                    label='Date to'
                    value={selectedEndDate}
                    onChange={(newValue: any) => setSelectedEndDate(newValue)}
                    disabled
                  />
                </LocalizationProvider>
                <Autocomplete
                  disableClearable
                  size='small'
                  value={departmentListValue}
                  onChange={(_event: ChangeEvent<{}>, newValue: string) => {
                    setDepartmentListValue(newValue);
                    const syntheticEvent = {
                      target: {
                        value: newValue,
                      },
                    } as ChangeEvent<HTMLInputElement>; // Type assertion to satisfy the type system
                    handleDepartmentListChange(syntheticEvent);
                  }}
                  inputValue={settingsInputValue}
                  onInputChange={(_event: any, newInputValue: string) => {
                    setSettingsInputValue(newInputValue);
                  }}
                  options={listDepartment || []}
                  sx={{
                    width: { xs: '100%', md: 227 },
                    '& .MuiAutocomplete-endAdornment button': {
                      border: 0,
                      color: '#161C248F',
                      bgcolor: 'transparent',
                    },
                  }}
                  renderInput={(params: any) => (
                    <TextField {...params} label='Department' />
                  )}
                />
                <TracesStatusChips
                  activeStatus={activeStatus}
                  handleStatusClick={handleStatusClick}
                />
              </Stack>

              {/* DATATABLE */}
              <Stack sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <DataTable
                  apiRef={apiRef}
                  columns={columns}
                  getRowId={(row) => row.traceDetailsID}
                  rows={
                    isError
                      ? []
                      : getTracesData?.data?.traces?.map(
                          ({ trace }: any) => trace,
                        ) || []
                  }
                  pageSize={100}
                  loading={isGetting || isGetFetching}
                  onRowSelectionModelChange={handleSelectionChange}
                  checkboxSelection={false}
                  slotProps={{
                    row: {
                      onContextMenu: handleContextMenu,
                      onTouchStart: handleTouchStart,
                      onTouchEnd: handleTouchEnd,
                      style: { cursor: 'context-menu' },
                    },
                  }}
                  sx={{
                    '&.MuiDataGrid-root': {
                      p: 0,
                    },
                  }}
                  // height={300}
                  fullHeight
                />
              </Stack>
            </Stack>

            {/* TRACES DETAILS */}
            {viewTraces && (
              <Stack
                gap={1}
                direction={{ xs: 'column' }}
                minWidth={{ xs: '100%', sm: 300 }}
                width={{ xs: '100%', sm: 300 }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={1}
                  sx={{
                    backgroundColor: bgColor,
                    p: '8px 16px',
                    borderRadius: '4px',
                    width: '100%',
                  }}
                >
                  <Typography fontWeight={500}>Trace Messages</Typography>
                </Stack>
                <Stack direction='column' spacing={1} p={2}>
                  <Stack>
                    <Typography display='flex'>
                      {matchingTrace ? matchingTrace?.trace?.shortName : ''}
                    </Typography>
                    <Typography
                      color='textSecondary'
                      fontSize={12}
                      display='flex'
                    >
                      {matchingTrace
                        ? moment(matchingTrace?.trace?.date).format(
                            'MMM DD, YYYY',
                          ) +
                          ' ' +
                          moment(
                            matchingTrace?.trace?.recurringTime,
                            'HH:mm:ss.SSS',
                          ).format('hh:mm A')
                        : ''}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography fontWeight={500} pt={1}>
                      {matchingTrace ? matchingTrace?.trace?.message : ''}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  ref={containerRef}
                  direction='column'
                  spacing={2}
                  maxHeight='400px'
                  overflow='auto'
                >
                  {matchingTrace?.trace?.notes &&
                    matchingTrace?.trace?.notes.map((note: any, index: any) => {
                      const dateTime = note?.dateTime;
                      const isValidDate = moment(dateTime).isValid();
                      return (
                        <Stack
                          key={index}
                          direction='column'
                          p={2}
                          flex={1}
                          spacing={1}
                          sx={{
                            backgroundColor: '#f5f5f5',
                            p: '8px 16px',
                            borderRadius: '4px',
                            width: '100%',
                          }}
                        >
                          <Stack width='100%'>
                            <Typography>{note?.shortName}</Typography>
                            {isValidDate && (
                              <Typography color='textSecondary' fontSize={12}>
                                {moment(dateTime).format('MMM DD, YYYY') +
                                  ' ' +
                                  moment(dateTime, 'HH:mm:ss.SSS').format(
                                    'hh:mm A',
                                  )}
                              </Typography>
                            )}
                          </Stack>
                          <Stack>
                            <Typography fontWeight={500} pt={1}>
                              {note?.notes}
                            </Typography>
                          </Stack>
                        </Stack>
                      );
                    })}
                </Stack>

                <Stack
                  spacing={1}
                  flex={1}
                  justifyContent='flex-end'
                  width='100%'
                >
                  <Stack direction='row' spacing={1}>
                    <TextField
                      label='Comment'
                      variant='outlined'
                      fullWidth
                      value={commentValue}
                      onChange={handleChange}
                      error={error} // Set error prop to display error state
                      helperText={error ? 'Comment cannot be empty' : ''}
                      size='small'
                    />
                    <Button
                      onClick={handleSubmit}
                      variant='contained'
                      color='primary'
                    >
                      <Send />
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Stack>

          {/* BUTTON */}
          <Stack
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 20,
              bgcolor: white,
              borderRadius: '4px',
            }}
          >
            <Divider
              sx={{
                borderStyle: 'dashed',
              }}
            />
            <Stack
              sx={{ p: 3 }}
              direction={{ xs: 'column', lg: 'row' }}
              justifyContent='flex-end'
            >
              {viewTraces && (
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  flex={1}
                  spacing={1}
                >
                  <Stack direction='row' gap={1} alignItems='center'>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        color: '#212B36',
                      }}
                    >
                      Created By :
                    </Typography>
                    <Chip
                      label={
                        matchingTrace ? matchingTrace?.trace?.shortName : ''
                      }
                      sx={{
                        p: '4px',
                        cursor: 'default',
                        height: '20px',
                        '& .MuiChip-label': {
                          fontSize: '12px',
                          fontFamily: 'Poppins',
                        },
                      }}
                    />
                  </Stack>
                  <Stack direction='row' gap={1} alignItems='center'>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        color: '#212B36',
                      }}
                    >
                      Stay Dates:
                    </Typography>
                    <Chip
                      label={
                        matchingTrace
                          ? moment(matchingTrace?.trace?.dateFrom).format(
                              'MMM DD, YYYY',
                            ) +
                            '-' +
                            moment(matchingTrace?.trace?.dateTo).format(
                              'MMM DD, YYYY',
                            )
                          : ''
                      }
                      sx={{
                        p: '4px',
                        cursor: 'default',
                        height: '20px',
                        '& .MuiChip-label': {
                          fontSize: '12px',
                          fontFamily: 'Poppins',
                        },
                      }}
                    />
                  </Stack>
                </Stack>
              )}
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1}
                justifyContent='flex-end'
              >
                <Button
                  onClick={handleCloseClick}
                  variant='outlined'
                  color='primary'
                >
                  Close
                </Button>
                <Button
                  id='trace-list_add-new-btn'
                  onClick={() => {
                    removeSelectedData();
                    createOrEditTrace();
                  }}
                  variant='contained'
                  color='primary'
                  // disabled={!traceDetails}
                >
                  Add New
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </ModalWrapper>

      <>
        <TracesResolveConfirmationModal
          isOpen={isResolveModalOpen}
          onClose={() => {
            removeSelectedData();
            handleCloseResolveTrace();
          }}
          traceData={tracesDetails?.traces?.find(
            (trace: any) => trace?.trace?.traceDetailsID == selectedData,
          )}
        />
        <TracesUnresolveConfirmationModal
          isOpen={isUnResolveModalOpen}
          onClose={() => {
            removeSelectedData();
            handleCloseUnResolveTrace();
          }}
          traceData={tracesDetails?.traces?.find(
            (trace: any) => trace?.trace?.traceDetailsID == selectedData,
          )}
        />
        <TracesCreateEditDrawer
          isOpen={isDrawerOpen}
          onClose={() => {
            removeSelectedData();
            handleCloseCreateOrEditTrace();
          }}
          traceData={getTracesData?.data?.traces?.find(
            (trace: any) => trace?.trace?.traceDetailsID == selectedData,
          )}
          traceDetails={referenceID}
          traceTarget={traceTarget}
        />
        <MenuWrapper>
          {userCanEdit
            ? [
                <MenuItem key='t-edit' onClick={createOrEditTrace}>
                  Edit
                </MenuItem>,
              ]
            : null}
          {canResolved && isActive
            ? [
                <MenuItem key='t-resolve' onClick={resolveTrace}>
                  Resolve
                </MenuItem>,
                <MenuItem key='t-unresolve' onClick={unResolveTrace}>
                  Unresolve
                </MenuItem>,
              ]
            : null}
        </MenuWrapper>
      </>
    </>
  );
};

export default TracesListModal;
